import axios from 'axios'

import { captureError, SENTRY_SEVERITY, toApiError, toServerError } from '@/shared/sentry'
import { ResponseHeaderType } from '@/shared/types/api'
import { CMDTYPE } from '@/shared/types/cmdType'
import {
  ERRORCODE,
  ERRORCODE_CANCEL,
  ERRORCODE_CANCEL_VERIFY,
  ERRORCODE_REFUND,
  ERRORCODE_REFUND_VERIFY,
} from '@/shared/types/errorCode'

const stringValues = (enumObject: object) =>
  Object.values(enumObject).filter((value): value is string => typeof value === 'string')

/** 모니터링 대상 CmdType (전체) */
export const WATCH_CMD_TYPES = new Set<string>(stringValues(CMDTYPE))

/** 정의된 ErrCode (정상 실패 간주) */
export const KNOWN_ERR_CODES = new Set<string>([
  ...stringValues(ERRORCODE),
  ...stringValues(ERRORCODE_CANCEL_VERIFY),
  ...stringValues(ERRORCODE_CANCEL),
  ...stringValues(ERRORCODE_REFUND_VERIFY),
  ...stringValues(ERRORCODE_REFUND),
])

/**
 * HTTP 200 응답의 알 수 없는 ErrCode를 ApiError로 남기는 함수
 *
 * watch CmdType이 아니거나, ErrCode 목록에 있는 known 실패면 수집하지 않는다.
 * ErrorBoundary / HTTP 5xx와 역할을 섞지 않는다.
 * 서버가 내려준 ErrMsg를 그대로 메시지/extra에 남겨 Issue만 보고도 원인을 알 수 있게 한다.
 */
export function captureUnexpectedErrCode(header: ResponseHeaderType) {
  const { ResultCd, CmdType, ResultMsg } = header

  if (!CmdType || !WATCH_CMD_TYPES.has(CmdType)) return
  if (!ResultCd || KNOWN_ERR_CODES.has(ResultCd)) return

  captureError(
    toApiError(
      new Error(
        `[API] Unexpected error | cmdType=${cmdTypeLabel(CmdType)} errCode=${ResultCd} ${
          ResultMsg ? ` : ${ResultMsg}` : ''
        }`,
      ),
    ),
    {
      level: SENTRY_SEVERITY.Warning,
      tags: { cmdType: String(CmdType), errCode: String(ResultCd) },
      fingerprint: ['api-unexpected-error', String(CmdType), String(ResultCd)],
    },
  )
}

const HTTP_STATUS_LABEL: Record<number, string> = {
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
}

/**
 * axios HTTP 5xx만 ServerError로 남기는 함수
 *
 * offline / ERR_NETWORK / abort / 4xx는 제외한다.
 */
export function captureServerError(error: unknown, cmdType: string) {
  if (!axios.isAxiosError(error)) return

  const status = error.response?.status
  if (!status || status < 500 || status >= 600) return

  const statusLabel = HTTP_STATUS_LABEL[status] ?? 'Server Error'

  captureError(toServerError(new Error(`[Server] HTTP ${status} ${statusLabel}`)), {
    level: SENTRY_SEVERITY.Error,
    tags: {
      httpStatus: String(status),
      cmdType: String(cmdType),
    },
    extra: {
      method: error.config?.method,
      response: error.response?.data,
    },
    fingerprint: ['server-error', String(cmdType), String(status)],
  })
}

/**
 * CmdType 숫자를 Sentry 표기용 라벨로 변환한다.
 *
 * @example
 * cmdTypeLabel(10013) // "requestSendCertNumber (10013)"
 * cmdTypeLabel(9999)  // "9999"
 */

export function cmdTypeLabel(cmdType: string): string {
  for (const [name, value] of Object.entries(CMDTYPE)) {
    if (String(value) === cmdType) return `${name} (${cmdType})`
  }
  return String(cmdType)
}
